import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";

export const LOST_REASONS = [
  "Price too high",
  "Not interested",
  "Using competitor",
  "No budget",
  "No response / Ghosted",
  "Workshop not ready yet",
  "Wants to study / compare first",
  "Wrong contact / Invalid number",
  "Out of target market",
  "Other",
];

/**
 * Modal shown when a lead is being marked as Lost.
 * Captures reason category, "not actual workshop" flag, and free-text remark.
 *
 * Props:
 *  - open / onOpenChange
 *  - initial: { lost_reason, lost_not_actual_workshop, lost_remark }
 *  - mode: "create" | "edit"
 *  - onConfirm({ lost_reason, lost_not_actual_workshop, lost_remark })
 */
export default function LostReasonDialog({ open, onOpenChange, initial, mode = "create", onConfirm }) {
  const [reason, setReason] = useState("");
  const [notActualWorkshop, setNotActualWorkshop] = useState(false);
  const [remark, setRemark] = useState("");

  // Reset / hydrate form when dialog opens
  useEffect(() => {
    if (!open) return;
    setReason(initial?.lost_reason || "");
    setNotActualWorkshop(!!initial?.lost_not_actual_workshop);
    setRemark(initial?.lost_remark || "");
  }, [open, initial]);

  const isEdit = mode === "edit";
  const canConfirm = !!reason;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="lost-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            {isEdit ? "Edit Lost Details" : "Mark as Lost"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update why this lead was lost. This helps categorise pipeline drop-offs."
              : "Tell us why this lead was lost so we can categorise causes later."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-gray-500">Reason <span className="text-red-500">*</span></Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="border-gray-300 rounded-lg text-sm h-9 mt-1" data-testid="lost-reason-select">
                <SelectValue placeholder="Pick a reason" />
              </SelectTrigger>
              <SelectContent>
                {LOST_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <label
            className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
            data-testid="lost-not-actual-workshop-label"
          >
            <input
              type="checkbox"
              checked={notActualWorkshop}
              onChange={(e) => setNotActualWorkshop(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              data-testid="lost-not-actual-workshop-checkbox"
            />
            <span className="text-xs text-amber-900 leading-relaxed">
              <strong>This lead is not an actual workshop.</strong>
              <br />
              <span className="text-amber-700">e.g. someone just trying out registration, competitor, person studying the system, etc.</span>
            </span>
          </label>

          <div>
            <Label className="text-xs text-gray-500">Remark / Note</Label>
            <Textarea
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Add any extra context (optional)…"
              rows={3}
              className="border-gray-300 rounded-lg text-sm mt-1 resize-none"
              data-testid="lost-remark-textarea"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="lost-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={!canConfirm}
            data-testid="lost-confirm-btn"
            onClick={() => {
              onConfirm({
                lost_reason: reason,
                lost_not_actual_workshop: notActualWorkshop,
                lost_remark: remark.trim() || null,
              });
            }}
          >
            {isEdit ? "Save Changes" : "Confirm Lost"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
